import {Component, OnInit, Input, DoCheck, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NlrpFieldRecordLocalitiesService} from './field-record-localities.service';
import {Locality} from './locality.model';
import {NlrTypeheadDirective} from '../../common/directive/nlr-typehead.directive';
import {NlrUppercaseDirective} from '../../common/directive/nlr-uppercase.directive';
import {Observable, OperatorFunction, debounceTime, distinctUntilChanged, map, BehaviorSubject} from 'rxjs';
import {selectedLodgementType} from '../../lodge-field-record/lodge-field-record.model';
import {PlanModel} from '../../common/service/plan-model.model';
import {selectedPurpose, PlanType, FormerTenureSubjectOfPlans} from '../../lodge-field-record/lodge-field-record.model';
import {Subject} from 'rxjs';
import {isValidString} from '../../common/service/data-util';

@Component({
    selector: 'nlr-field-record-localities',
    imports: [FormsModule, CommonModule, NlrTypeheadDirective, NlrUppercaseDirective],
    templateUrl: './field-record-localities.component.html',
    styleUrl: './field-record-localities.component.scss'
})
export class FieldRecordLocalitiesComponent implements OnInit, DoCheck, OnDestroy {
    allLocalities: Locality[] = undefined;
    planModel: PlanModel = undefined;
    submitted = false;
    localityOptions = [];
    previousPurpose = undefined;
    previousSubjectPlans = undefined;
    previousFormerTenures = undefined;
    selectedPurposeSubjectPlanTypes = [];
    selectedLocalityName = [];
    isLocalityNameSelected = [];

    public planModeldata = new BehaviorSubject<PlanModel>(undefined);

    @Input()
    model: PlanModel;
    @Input()
    selectedLodgementType: selectedLodgementType;
    @Input()
    selectedPurpose: selectedPurpose;
    @Input()
    planTypes: PlanType[];
    @Input()
    formerTenureSubjectOfPlans: FormerTenureSubjectOfPlans[];

    localityNameSearch: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term =>
                this.getLocalityOptions()
                    .filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
                    .slice(0, 10)
            )
        );

    constructor(private nlrpFieldRecordLocalitiesService: NlrpFieldRecordLocalitiesService) {}

    ngOnInit(): void {
        if (typeof this.model.localities === undefined) {
            this.model.localities = [];
        }

        if (this.model.localities.length === 0) {
            this.addLocality(0);
        }

        this.nlrpFieldRecordLocalitiesService.getAllLocalities().subscribe(allLocalities => {
            this.allLocalities = allLocalities;
        });
    }

    getLocalityOptions() {
        this.isLocalityNameSelected = [];
        if (!this.allLocalities) {
            return [];
        }

        const modelLocalityNames = this.model.localities.map(function (locality) {
            return locality.localityName;
        });

        return this.allLocalities
            .filter(function (locality) {
                return modelLocalityNames.indexOf(locality.name) === -1;
            })
            .map(v => {
                return v.name;
            });
    }

    ngOnDestroy(): void {
        // if (this.planModel.localities) {
        //     delete this.planModel.localities;
        // }
    }

    ngDoCheck(): void {
        if (!this.allLocalities) {
            return;
        }

        let filteredSubjectPlans = this.model.subjectPlans
            ? this.model.subjectPlans.filter(v => {
                  return v.planType && v.planNumber;
              })
            : [];

        let filteredFormerTenures = this.model.formerTenures
            ? this.model.formerTenures.filter(v => {
                  return v.parentPlanType && v.parentPlanNumber;
              })
            : [];

        if (
            this.model.purpose !== this.previousPurpose ||
            !this.previousSubjectPlans ||
            JSON.stringify(filteredSubjectPlans) !== JSON.stringify(this.previousSubjectPlans) ||
            !this.previousFormerTenures ||
            JSON.stringify(filteredFormerTenures) !== JSON.stringify(this.previousFormerTenures)
        ) {
            this.previousPurpose = this.model.purpose;
            this.previousSubjectPlans = [...filteredSubjectPlans];
            this.previousFormerTenures = [...filteredFormerTenures];
            let lotRequests = [];

            if (this.selectedPurpose.formerTenure === 'Implied') {
                let selectedPurposeSubjectPlanTypeCodes = (this.selectedPurpose.subjectPlanType || '').split(',');
                this.selectedPurposeSubjectPlanTypes = this.planTypes.filter(function (planType) {
                    return selectedPurposeSubjectPlanTypeCodes.indexOf(planType.id) !== -1;
                });

                lotRequests = filteredSubjectPlans.map(v => {
                    const planTypeId = this.selectedPurposeSubjectPlanTypes.filter(selectedPurposeSubjectPlanType => {
                        return v.planType === selectedPurposeSubjectPlanType.id;
                    })[0].id;
                    return {planType: planTypeId, planNumber: v.planNumber};
                });
            } else {
                lotRequests = filteredFormerTenures.map(v => {
                    return {planType: v.parentPlanType, planNumber: v.parentPlanNumber};
                });
            }
            const resultSubject = new Subject();
            this.nlrpFieldRecordLocalitiesService.getLotLocalitiesByLotRequestList(lotRequests, resultSubject);
            resultSubject.subscribe({
                next: localities => {
                    this.model.localities = localities;
                    if (this.model.localities.length === 0) {
                        this.addLocality(0);
                    } else {
                        this.model.localities.forEach((l, i) => {
                            this.isLocalityNameSelected[i] = true;
                        });
                    }
                    console.debug('Success getting localities:', localities);
                },
                error: error => {
                    console.error('Error getting localities:', error);
                }
            });
        }
    }

    isLocalityRequired() {
        return this.selectedLodgementType.localityMandatory;
    }

    addLocality(idx: number) {
        this.model.localities.splice(arguments.length ? idx + 1 : idx, 0, {localityName: ''});
    }

    removeLocality(idx: number) {
        this.model.localities.splice(idx, 1);
        if (this.model.localities.length === 0) {
            this.addLocality(0);
        }
    }

    getLocalities(model: PlanModel) {
        this.nlrpFieldRecordLocalitiesService.getAllLocalities().subscribe({
            next: data => {
                this.allLocalities = data;

                if (!this.allLocalities) {
                    this.localityOptions = [];
                }

                let modelLocalityNames = this.planModel.localities.map(function (locality) {
                    return locality.localityName;
                });

                let filteredLocalityNames = this.allLocalities.filter(function (locality) {
                    return modelLocalityNames.indexOf(locality.localityName) === -1;
                });

                this.localityOptions = filteredLocalityNames
                    .map(locality => locality.localityName)
                    .filter((value, index, self) => self.indexOf(value) === index);
                console.debug('Success getting localities:', this.allLocalities);
            },
            error: error => {
                console.error('Error getting localities:', error);
            }
        });
        return model;
    }

    getLocalityName(index: number) {
        return this.model.localities[index].localityName === undefined ? '' : this.model.localities[index].localityName;
    }

    localitySelected(localityName: string, index: number) {
        this.model.localities[index].localityName = localityName;
        if (isValidString(this.model.localities[index].localityName)) this.isLocalityNameSelected[index] = true;
    }

    checkLocalityName(index: number) {
        if (
            (this.model.localities[index].localityName === '' && this.isLocalityRequired()) ||
            (this.model.localities[index].localityName === undefined && this.isLocalityRequired())
        ) {
            this.isLocalityNameSelected[index] = false;
            return true;
        }
        return false;
    }

    isLocalityNameInvalid(index: number) {
        return this.isLocalityNameSelected.length > 0 ? !isValidString(this.model.localities[index].localityName) : false;
    }

    onLocalityNameChange(value: string, index: number) {
        if (!isValidString(value)) {
            this.isLocalityNameSelected[index] = false;
        }
    }
}
